import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { isPast, isToday } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { BOOKING_CHANNEL_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/types'
import { LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { SESSION_TYPE_MESSAGES } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { getDbtTeensSessionLabel } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/getDbtTeensSessionLabel'
import { CalendarCardAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Avatar, avatarAltTextGeneric } from '../../atoms/icons/Avatar'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type DbtTeensSessionCardProps = {
  appointment: CalendarCardAppointment
  attendeeNames?: string[]
  recipientName?: string
  onPress?: () => void
  trailingAccessory?: ReactNode
  testID?: string
}

const INLINE_SEPARATOR = ' · '
const ATTENDEE_SEPARATOR = ' • '
const DBT_TEENS_FAMILY_ATTENDEE_COUNT = 2

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  minHeight: '80px',
  overflow: 'hidden',
  backgroundColor: getAuroraAwareBackgroundColor(
    theme.isAuroraTheme,
    theme.colors.backgroundPrimary,
    theme.colors.cardBackgroundPrimary,
  ),
  borderWidth: theme.isAuroraTheme ? 0 : '1px',
  borderColor: theme.colors.borderDefault,
  borderRadius: theme.breakpoints.isMinWidthLaptop ? theme.spacing['16px'] : theme.spacing['12px'],
}))

const CardPressable = styled(PressableOpacity)({
  width: '100%',
})

const RecipientContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  width: '100%',
  alignItems: 'center',
  paddingHorizontal: spacing['16px'],
  paddingVertical: spacing['4px'],
  backgroundColor: colors.cardBackgroundPrimary,
}))

const CardBody = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '100%',
  padding: spacing['16px'],
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

const InformationContainer = styled.View({
  flex: 1,
  minWidth: 0,
})

const AvatarContainer = styled.View<{ hasAttendeeCount: boolean }>(({ hasAttendeeCount }) => ({
  paddingRight: hasAttendeeCount ? '18px' : 0,
  position: 'relative',
}))

const AttendeeCountContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  alignItems: 'center',
  backgroundColor: colors.components.badge.neutral.background,
  borderColor: colors.borderDefault,
  borderRadius: '12px',
  borderWidth: '1px',
  bottom: '-4px',
  height: '24px',
  justifyContent: 'center',
  position: 'absolute',
  right: '6px',
  width: '24px',
}))

const TrailingAccessoryContainer = styled.View({
  flexShrink: 0,
  marginLeft: 'auto',
})

/** Shared, appointment-driven presentation for a DBT Teens session. */
export const DbtTeensSessionCard: FunctionComponent<DbtTeensSessionCardProps> = ({
  appointment,
  attendeeNames,
  recipientName,
  onPress,
  trailingAccessory,
  testID = 'DbtTeensSessionCard',
}) => {
  const { formatDate, formatMessage } = useIntl()
  const { breakpoints, colors } = useTheme()
  const { appointmentTodayText } = colors.components.calendarCard
  const dateTime = getDateFromAppointment(appointment)
  const appointmentIsPast = isPast(dateTime)
  const appointmentIsToday = isToday(dateTime)
  const appointmentIsCanceled = appointment.appointmentStatus === 'canceled'
  const shouldHideTime =
    appointment.bookingChannel === BOOKING_CHANNEL_TYPE.PP_API_AUTO_APPT_CREATION && appointmentIsPast
  const formattedDate = formatDate(dateTime, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const formattedTime = shouldHideTime
    ? undefined
    : formatDate(dateTime, {
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
      })
  const dateTimeLabel = formattedTime ? `${formattedDate}${INLINE_SEPARATOR}${formattedTime}` : formattedDate
  const sessionLabelMessage = getDbtTeensSessionLabel(appointment)
  const sessionLabel = sessionLabelMessage ? formatMessage(sessionLabelMessage) : undefined
  const normalizedAttendeeNames = attendeeNames?.map((name) => name.trim()).filter(Boolean)
  const sessionDisplayLabel =
    sessionLabel && normalizedAttendeeNames?.length
      ? `${sessionLabel}${ATTENDEE_SEPARATOR}${normalizedAttendeeNames.join(', ')}`
      : sessionLabel
  const meetingFormatMessage = SESSION_TYPE_MESSAGES[appointment.meetingFormat]
  const modalityLabel = meetingFormatMessage ? formatMessage(meetingFormatMessage) : appointment.meetingFormat
  const sessionDetailsLabel = formatMessage(
    {
      defaultMessage: '{modality} · {duration} min',
      description: 'DBT Teens session modality and duration',
    },
    {
      modality: modalityLabel,
      duration: appointment.appointmentDuration,
    },
  )
  const normalizedRecipientName = recipientName?.trim()
  const recipientLabel = normalizedRecipientName
    ? formatMessage(
        {
          defaultMessage: 'For {recipientName} only',
          description: 'Label indicating that only the named dependent attends the session',
        },
        { recipientName: normalizedRecipientName },
      )
    : undefined
  const todayLabel = appointmentIsToday
    ? formatMessage({
        defaultMessage: 'TODAY',
        description: 'Label indicating that a session occurs today',
      })
    : undefined
  const statusLabel = appointmentIsCanceled
    ? formatMessage({
        defaultMessage: 'Canceled',
        description: 'Label showing the session was canceled',
      })
    : appointmentIsPast
      ? formatMessage({
          defaultMessage: 'Attended',
          description: 'Label showing the session was attended',
        })
      : undefined
  const providerDisplayName = appointment.avatarDetails?.displayName || appointment.provider?.display_name || ''
  const providerImage = appointment.image?.imageUrl ?? appointment.provider?.lyraProviderImage?.imageUrl
  const avatarDetails =
    appointment.avatarDetails ??
    (providerDisplayName || providerImage
      ? {
          src: providerImage ?? '',
          displayName: providerDisplayName,
        }
      : undefined)
  const providerID = appointment.providerID ?? appointment.provider?.lyra_id ?? appointment.lyraProviderId
  const avatarSize = breakpoints.isMinWidthLaptop ? 60 : 48
  const shouldShowFamilyAttendeeCount = appointment.sessionType === LTSessionTypes.DBT_TEENS_FAMILY_SESSION
  const familyAttendeeCountLabel = shouldShowFamilyAttendeeCount
    ? formatMessage(
        {
          defaultMessage: '+{count}',
          description: 'Count of the caregiver and teen attending a DBT Teens family session',
        },
        { count: DBT_TEENS_FAMILY_ATTENDEE_COUNT },
      )
    : undefined
  const familyAttendeesLabel = shouldShowFamilyAttendeeCount
    ? formatMessage(
        {
          defaultMessage: '{count} additional attendees',
          description: 'Accessible description of the caregiver and teen attending a DBT Teens family session',
        },
        { count: DBT_TEENS_FAMILY_ATTENDEE_COUNT },
      )
    : undefined
  const accessibilityLabel = [
    recipientLabel,
    providerDisplayName,
    dateTimeLabel,
    todayLabel,
    sessionDisplayLabel,
    sessionDetailsLabel,
    statusLabel,
    familyAttendeesLabel,
  ]
    .filter(Boolean)
    .join(', ')

  const contents = (
    <>
      {recipientLabel && (
        <RecipientContainer testID={tID('DbtTeensSessionCard-recipient-label')}>
          <BodyText
            text={recipientLabel}
            size={BodyTextSize.BADGE}
            color={colors.textSecondary}
            textAlign='center'
            medium
          />
        </RecipientContainer>
      )}
      <CardBody testID={tID('DbtTeensSessionCard-information-block')}>
        {avatarDetails && !avatarDetails.hide && (
          <AvatarContainer hasAttendeeCount={shouldShowFamilyAttendeeCount}>
            <Avatar
              details={avatarDetails}
              accessibilityLabel={formatMessage(avatarAltTextGeneric, { name: avatarDetails.displayName })}
              size={avatarSize}
              providerID={providerID}
            />
            {shouldShowFamilyAttendeeCount && (
              <AttendeeCountContainer testID={tID('DbtTeensSessionCard-attendee-count')}>
                <BodyText text={familyAttendeeCountLabel} size={BodyTextSize.CAPTION} color={colors.textSecondary} />
              </AttendeeCountContainer>
            )}
          </AvatarContainer>
        )}
        <InformationContainer>
          <BodyText
            size={BodyTextSize.DEFAULT}
            color={colors.textPrimary}
            testID={tID('DbtTeensSessionCard-appointment-time')}
          >
            {dateTimeLabel}
            {appointmentIsToday && (
              <>
                {INLINE_SEPARATOR}
                <BodyText
                  text={todayLabel}
                  color={appointmentTodayText.text}
                  size={BodyTextSize.SMALL}
                  testID={tID('DbtTeensSessionCard-Today')}
                />
              </>
            )}
          </BodyText>
          {sessionDisplayLabel && (
            <BodyText
              text={sessionDisplayLabel}
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              testID={tID('DbtTeensSessionCard-subtitle')}
            />
          )}
          <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary}>
            {sessionDetailsLabel}
            {statusLabel && (
              <>
                {INLINE_SEPARATOR}
                <BodyText
                  text={statusLabel}
                  size={BodyTextSize.SMALL}
                  color={appointmentIsCanceled ? colors.textWarning : colors.textSecondary}
                  testID={tID(appointmentIsCanceled ? 'DbtTeensSessionCard-cancelled' : 'DbtTeensSessionCard-attended')}
                />
              </>
            )}
          </BodyText>
        </InformationContainer>
        {trailingAccessory && <TrailingAccessoryContainer>{trailingAccessory}</TrailingAccessoryContainer>}
      </CardBody>
    </>
  )

  return (
    <Container accessibilityLabel={onPress ? undefined : accessibilityLabel} testID={onPress ? undefined : tID(testID)}>
      {onPress ? (
        <CardPressable
          onPress={onPress}
          accessibilityLabel={accessibilityLabel}
          accessibilityRole='button'
          testID={tID(testID)}
        >
          {contents}
        </CardPressable>
      ) : (
        contents
      )}
    </Container>
  )
}
